import { createContext, ReactNode, useContext, useState } from "react";

import Router from "next/router";
import { destroyCookie, parseCookies, setCookie } from "nookies";

import { loginRequest } from "@/hooks/Auth";
import { useIsomorphicLayoutEffect } from "@/hooks/Custom";

type SignInCredentials = {
  UserEmail: string;
  password: string;
};

type signInReturn = {
  status: boolean;
  message: string;
};

type AuthContextData = {
  signIn: (credentials: SignInCredentials) => Promise<signInReturn>;
  signOut: () => void;
  isAuthenticated: boolean;
  clientId: string;
  clientToken: string;
  refreshToken: string;
  statusMessage: string;
  loading: boolean;
  email: string;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const cookies = parseCookies();

  const CLIENT_TOKEN = process.env.NEXT_PUBLIC_CLIENT_TOKEN as string;
  const CLIENT_REFRESH_TOKEN = process.env.NEXT_PUBLIC_CLIENT_REFRESH_TOKEN as string;
  const CLIENT_ID = process.env.NEXT_PUBLIC_CLIENT_ID as string;
  const CLIENT_EMAIL = process.env.NEXT_PUBLIC_CLIENT_EMAIL as string;
  const COOKIE_MAX_AGE = process.env.NEXT_PUBLIC_CLIENT_MAX_AGE as unknown as number;

  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState(null);
  const [clientToken, setClientToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [email, setEmail] = useState(null);

  useIsomorphicLayoutEffect(() => {
    const token = cookies[CLIENT_TOKEN];
    const id = cookies[CLIENT_ID];
    const refresh = cookies[CLIENT_REFRESH_TOKEN];
    const email = cookies[CLIENT_EMAIL];
    if (token && id) {
      setClientToken(token);
      setClientId(id);
      setRefreshToken(refresh);
      setEmail(email);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, []);

  async function signIn({ UserEmail, password }: SignInCredentials): Promise<signInReturn> {
    const { token, refreshToken, id, message, status, email } = await loginRequest(
      UserEmail,
      password
    );

    if (token && id) {
      await setSession(token, id, refreshToken, email);
      setIsAuthenticated(status);
    } else {
      setIsAuthenticated(false);
      setStatusMessage(message);
    }

    return { status, message };
  }

  async function signOut() {
    destroyCookie(null, CLIENT_TOKEN, { path: "/" });
    destroyCookie(null, CLIENT_REFRESH_TOKEN, { path: "/" });
    destroyCookie(null, CLIENT_ID, { path: "/" });
    destroyCookie(null, CLIENT_EMAIL, { path: "/" });
    setIsAuthenticated(false);
    Router.reload();
  }

  async function setSession(token: string, clientId: string, refreshToken: string, email: string) {
    setClientId(clientId);
    setClientToken(token);
    setRefreshToken(refreshToken);

    setCookie(null, CLIENT_TOKEN, token, {
      maxAge: COOKIE_MAX_AGE,
      path: "/"
    });

    setCookie(null, CLIENT_REFRESH_TOKEN, refreshToken, {
      maxAge: COOKIE_MAX_AGE,
      path: "/"
    });

    setCookie(null, CLIENT_ID, clientId, {
      maxAge: COOKIE_MAX_AGE,
      path: "/"
    });

    setCookie(null, CLIENT_EMAIL, email, {
      maxAge: COOKIE_MAX_AGE,
      path: "/"
    });
  }

  return (
    <AuthContext.Provider
      value={{
        signIn,
        signOut,
        isAuthenticated,
        clientId,
        clientToken,
        refreshToken,
        statusMessage,
        loading,
        email
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}
